<template>
  <div class="calendar-month">
    <div class="calendar-month__headers">
      <div
        v-for="dayName in dayNames"
        :key="dayName"
        class="calendar-month__header"
      >
        {{ dayName }}
      </div>
    </div>

    <div class="calendar-month__grid">
      <div
        v-for="(day, index) in calendarDays"
        :key="day.key"
        class="calendar-month__cell"
        :class="[
          isWeekend(index) ? 'calendar-month__cell--weekend' : '',
          day.isCurrentMonth ? '' : 'calendar-month__cell--muted',
          getAppointmentsByDay(day.date).length
            ? 'calendar-month__cell--has-appointments'
            : '',
        ]"
        @click="selectDay(day.date)"
      >
        <div class="calendar-month__day-number">
          {{ day.date.format('D') }}
        </div>

        <div class="calendar-month__appointments">
          <div
            v-for="(appointment, appointmentIndex) in getAppointmentsByDay(
              day.date
            )"
            :key="appointmentKey(appointment, appointmentIndex)"
            class="calendar-month__appointment"
            :style="monthAppointmentStyle(appointment)"
          >
            <span
              class="calendar-month__appointment-title"
              :style="{ color: getAppointmentColor(appointment) }"
            >
              <i class="fas fa-folder-open mr-1" />
              {{ appointmentTitle(appointment) }}
            </span>
            <span class="calendar-month__appointment-time">
              <i
                class="fas fa-clock mr-1"
                :style="{ color: getAppointmentColor(appointment) }"
              />
              {{ formatAppointmentTime(appointment.startTime) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import calendarAppointmentDisplayMixin from '../mixins/calendarAppointmentDisplayMixin';

export default {
  name: 'CompuCalendarModeMonth',
  mixins: [calendarAppointmentDisplayMixin],
  props: {
    value: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    currentDate: { type: Date, required: true, default: () => new Date() },
  },
  computed: {
    model() {
      return this.value;
    },
    dayNames() {
      return [
        'Domingo',
        'Lunes',
        'Martes',
        'Miércoles',
        'Jueves',
        'Viernes',
        'Sábado',
      ];
    },
    calendarDays() {
      const selectedDate = moment(this.currentDate);
      const firstDay = moment(selectedDate)
        .startOf('month')
        .startOf('day');
      const lastDay = moment(selectedDate)
        .endOf('month')
        .startOf('day');
      const days = [];
      const current = moment(firstDay).subtract(firstDay.day(), 'days');
      const calendarEnd = moment(lastDay).add(6 - lastDay.day(), 'days');

      while (current.isSameOrBefore(calendarEnd, 'day')) {
        days.push({
          date: moment(current),
          key: current.format('YYYY-MM-DD'),
          isCurrentMonth: current.isSame(selectedDate, 'month'),
        });

        current.add(1, 'day');
      }

      return days;
    },
  },
  methods: {
    isWeekend(index) {
      const dayIndex = index % 7;

      return dayIndex === 0 || dayIndex === 6;
    },
    getAppointmentsByDay(date) {
      const appointments = this.model.filter((appointment) =>
        moment(appointment.appointmentDate).isSame(date, 'day')
      );
      const groups = appointments.reduce((result, appointment) => {
        const key = [
          appointment.meetingTypeId || appointment.meetingTypeName || 'none',
          appointment.startTime || 'none',
        ].join('|');

        if (!result[key]) {
          result[key] = {
            ...appointment,
            id: key,
            groupedAppointments: [],
          };
        }

        result[key].groupedAppointments.push(appointment);

        return result;
      }, {});

      return Object.values(groups);
    },
    selectDay(date) {
      if (!this.getAppointmentsByDay(date).length) {
        return;
      }

      this.$emit(
        'select-day',
        moment(date)
          .startOf('day')
          .toDate()
      );
    },
    monthAppointmentStyle(appointment) {
      const color = this.getAppointmentColor(appointment);

      return {
        border: `1px solid ${color}`,
        backgroundColor: this.getAppointmentBackground(appointment),
      };
    },
  },
};
</script>

<style scoped>
.calendar-month {
  min-height: 520px;
}

.calendar-month__headers {
  display: grid;
  grid-template-columns: repeat(7, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 8px;
  overflow-x: auto;
}

.calendar-month__header {
  background-color: #d9e5df !important;
  border-radius: 4px;
  color: #000;
  font-weight: 700;
  min-width: 120px;
  padding: 9px 8px;
  text-align: center;
}

.calendar-month__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(120px, 1fr));
  gap: 10px;
  overflow-x: auto;
}

.calendar-month__cell {
  background-color: #f4f6f3;
  border-radius: 4px;
  min-height: 164px;
  min-width: 120px;
  padding: 12px;
  position: relative;
}

.calendar-month__cell--weekend {
  background-color: #e2e2e2;
}

.calendar-month__cell--muted {
  opacity: 0.55;
}

.calendar-month__cell--has-appointments {
  cursor: pointer;
}

.calendar-month__day-number {
  color: #11291b;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-align: right;
}

.calendar-month__appointments {
  margin-top: 8px;
}

.calendar-month__appointment {
  align-items: center;
  border-radius: 4px;
  display: grid;
  font-size: 9px;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px;
  margin-bottom: 4px;
  min-height: 25px;
  padding: 2px 4px;
}

.calendar-month__appointment-title {
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-month__appointment-time {
  color: #11291b;
  font-weight: 700;
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .calendar-month__headers,
  .calendar-month__grid {
    grid-template-columns: repeat(7, minmax(150px, 1fr));
  }

  .calendar-month__header,
  .calendar-month__cell {
    min-width: 150px;
  }
}

@media (max-width: 768px) {
  .calendar-month__headers,
  .calendar-month__grid {
    grid-template-columns: repeat(7, minmax(170px, 1fr));
  }

  .calendar-month__header,
  .calendar-month__cell {
    min-width: 170px;
  }
}
</style>
